<!--
  Root gate is `draft` alone. It used to be `draft && draft.draftBody`,
  which meant a Draft whose DraftBody link had not resolved rendered
  NOTHING — no editor, no explanation — and the only other branch in
  this file was an empty <div>. See `ensureDraftSidesLoaded` for when
  that happens (any Draft that arrived by notification rather than by
  GET /draft, which includes every newly created one).
-->
<div class="projectDraft" *ngIf="draft" style="display:flex; flex-direction: column; height:100%; background: lightgray;">

  <!--
    ── Upload status ─────────────────────────────────────────
    `uploadState` / `uploadProgress` / `uploadMessage` /
    `uploadError` have been maintained by the component since the
    upload flow was written, and until now NOTHING rendered them.
    Every outcome - in progress, done, refused by the server, wrong
    file type - was invisible, which is why a failed upload looked
    exactly like "the file dialog closed and nothing happened".
  -->
  <div class="draft-upload-status" *ngIf="uploadState !== 'IDLE'" [ngClass]="'draft-upload-' + uploadState.toLowerCase()">
    <mat-icon class="draft-upload-icon" *ngIf="uploadState === 'UPLOADING'">cloud_upload</mat-icon>
    <mat-icon class="draft-upload-icon" *ngIf="uploadState === 'SUCCESS'">check_circle</mat-icon>
    <mat-icon class="draft-upload-icon" *ngIf="uploadState === 'ERROR'">error_outline</mat-icon>

    <div class="draft-upload-text">
      <div class="draft-upload-name" *ngIf="uploadingFile">{{uploadingFile.name}}</div>
      <div class="draft-upload-detail" *ngIf="uploadState === 'UPLOADING'">
        <span i18n="@@Uploading">Uploading</span><span>&nbsp;</span><span>{{uploadProgress | numberFormat}}%</span>
      </div>
      <div class="draft-upload-detail" *ngIf="uploadState === 'SUCCESS'">{{uploadMessage}}</div>
      <div class="draft-upload-detail" *ngIf="uploadState === 'ERROR'">{{uploadError}}</div>
      <mat-progress-bar *ngIf="uploadState === 'UPLOADING'" mode="determinate" [value]="uploadProgress"></mat-progress-bar>
    </div>

    <button mat-icon-button class="draft-upload-dismiss" (click)="clearUpload()" i18n-title="@@Dismiss" title="Dismiss">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!--
    Still resolving the DraftBody / DraftCover rows. Reachable for a
    few hundred ms on a newly created Draft, and permanently if the
    fetch fails - either way the operator gets told which, instead of
    an empty grey panel.
  -->
  <div class="draft-loading" *ngIf="!draft.draftBody">
    <mat-progress-spinner mode="indeterminate" diameter="28" color="primary"></mat-progress-spinner>
    <span i18n="@@Loading this Draft…">Loading this Draft…</span>
  </div>

  <!-- Book Settings collapsible section (fixed, non-scrolling) -->
  <div class="draft-settings-section" *ngIf="draft.draftBody">
    <div class="draft-settings-header" (click)="displaySettings = !displaySettings; onDataChange();">
      <mat-icon>{{ displaySettings ? 'expand_less' : 'expand_more' }}</mat-icon>
      <span i18n="@@Book Settings">Book Settings</span>
    </div>

    <div *ngIf="displaySettings" class="draft-settings-body">
      <div class="draft-settings-form">
        <mat-form-field appearance="outline" class="draft-settings-field">
          <mat-label i18n="@@Sub Title">Sub Title</mat-label>
          <input matInput i18n-placeholder="@@Sub Title" placeholder="Sub Title" maxlength="40" [(ngModel)]="draft.subtitle" [ngModelOptions]="{standalone: true}" (change)="saveDraft()">
        </mat-form-field>
        <mat-form-field appearance="outline" class="draft-settings-field">
          <mat-label i18n="@@Author">Author</mat-label>
          <input matInput i18n-placeholder="@@Author" placeholder="Author" maxlength="40" [(ngModel)]="draft.author" [ngModelOptions]="{standalone: true}" (change)="saveDraft()">
        </mat-form-field>
        <mat-form-field appearance="outline" class="draft-settings-field">
          <mat-label i18n="@@Note">Note</mat-label>
          <textarea matInput i18n-placeholder="@@Note" placeholder="Note" [(ngModel)]="draft.comment" [ngModelOptions]="{standalone: true}" rows="2" (change)="saveDraft()"></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline" class="draft-settings-field">
          <mat-label i18n="@@Language Code">Language Code</mat-label>
          <input matInput
                 [ngModel]="draft.languageCode"
                 (ngModelChange)="onDraftLanguageChange($event)"
                 [matAutocomplete]="draftLangAuto"
                 [ngModelOptions]="{standalone: true}"
                 i18n-placeholder="@@Language"
                 placeholder="Language"
                 (blur)="saveDraft()">
          <mat-autocomplete #draftLangAuto="matAutocomplete"
                            (optionSelected)="onDraftLanguageChange($event.option.value); saveDraft()">
            <mat-option *ngFor="let lang of filteredLanguages()" [value]="lang.iso639_3">
              {{ languageDisplayName(lang) }}
              <span class="lang-code-hint">({{ lang.iso639_3 }}<span *ngIf="lang.iso639_2">, {{ lang.iso639_2 }}</span>)</span>
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
      </div>

      <div class="draft-settings-toggles">
        <div class="draft-setting-group">
          <mat-label i18n="@@Interior">Interior</mat-label>
          <mat-button-toggle-group [(ngModel)]="draft.interiorType" (change)="onInteriorTypeChange($event.value)">
            <mat-button-toggle value="GRAY">
              <mat-icon>invert_colors</mat-icon><span i18n="@@Black and White">Black and White</span>
            </mat-button-toggle>
            <mat-button-toggle value="COLOR">
              <mat-icon>palette</mat-icon><span i18n="@@Color">Color</span>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <div class="draft-setting-group">
          <mat-label i18n="@@Interior Paper">Interior Paper</mat-label>
          <mat-button-toggle-group [(ngModel)]="draft.draftBody.paperType_ID" (change)="saveDraftBody()">
            <mat-button-toggle *ngIf="draft.interiorType==='GRAY'" [value]="1">
              <mat-icon>fitness_center</mat-icon><span i18n="@@Heavy">Heavy</span>
            </mat-button-toggle>
            <mat-button-toggle *ngIf="draft.interiorType==='GRAY'" [value]="2">
              <mat-icon>snowing</mat-icon><span i18n="@@Light">Light</span>
            </mat-button-toggle>
            <mat-button-toggle *ngIf="draft.interiorType==='GRAY'" [value]="4">
              <mat-icon>icecream</mat-icon><span i18n="@@Creme">Creme</span>
            </mat-button-toggle>
            <mat-button-toggle *ngIf="draft.interiorType==='COLOR'" [value]="5">
              <mat-icon>fitness_center</mat-icon><span i18n="@@60#">60#</span>
            </mat-button-toggle>
            <mat-button-toggle *ngIf="draft.interiorType==='COLOR'" [value]="6">
              <span i18n="@@80MatteText">80# Matte Text</span>
            </mat-button-toggle>
            <mat-button-toggle *ngIf="draft.interiorType==='COLOR'" [value]="7">
              <span i18n="@@80GlossText">80# Gloss Text</span>
            </mat-button-toggle>
            <mat-button-toggle *ngIf="draft.interiorType==='COLOR'" [value]="8">
              <span i18n="@@100GlossText">100# Gloss Text</span>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <div class="draft-setting-group">
          <mat-label i18n="@@Lamination">Lamination</mat-label>
          <mat-button-toggle-group [(ngModel)]="draft.draftCover.laminateType" (change)="saveDraftCover()">
            <mat-button-toggle value="GLOSS">
              <mat-icon>blur_on</mat-icon><span i18n="@@GLOSS">Gloss</span>
            </mat-button-toggle>
            <mat-button-toggle value="COLOR">
              <mat-icon>blur_off</mat-icon><span i18n="@@MATTE">Matte</span>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <div class="draft-setting-group">
          <mat-label i18n="@@Binding">Binding</mat-label>
          <mat-button-toggle-group [(ngModel)]="draft.bindingType" (change)="onBindingTypeChange($event.value)">
            <mat-button-toggle value="PERFECT">
              <mat-icon>book</mat-icon><span i18n="@@Perfect">Perfect</span>
            </mat-button-toggle>
            <mat-button-toggle value="COIL">
              <mat-icon>vertical_split</mat-icon><span i18n="@@Coil">Coil</span>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>

        <div class="draft-setting-group">
          <mat-label i18n="@@Cover Color Boost">Cover Color Boost</mat-label>
          <mat-slide-toggle
            [checked]="draft.draftCover.isColorBoosted === 'TRUE'"
            (change)="draft.draftCover.isColorBoosted = $event.checked ? 'TRUE' : 'FALSE'; saveDraftCover()">
          </mat-slide-toggle>
        </div>

        <div class="draft-setting-group">
          <mat-label i18n="@@Use ICC Profile">Use ICC Profile</mat-label>
          <mat-slide-toggle
            [checked]="draft.draftBody.useICC === 'TRUE'"
            (change)="draft.draftBody.useICC = $event.checked ? 'TRUE' : 'FALSE'; saveDraftBody()">
          </mat-slide-toggle>
        </div>
      </div>

      <!--
        Download-original-PDF actions. End-aligned within the
        Book Settings section footer so they sit at the right in
        LTR layouts and at the left in RTL layouts (per the
        project's start/end i18n convention). Each button is
        `*ngIf`-guarded on the underlying resource_ID so it only
        surfaces when there is actually a PDF to fetch — a Draft
        may have only a cover or only a body during initial
        setup. Button labels reuse the existing `@@Download
        Original Cover PDF` / `@@Download Original Body PDF`
        i18n IDs from ProjectBookComponent so we don't
        double-register the same strings under new ids.
      -->
      <div class="draft-settings-footer">
        <button *ngIf="draft.draftCover?.resource_ID"
                mat-stroked-button
                color="primary"
                class="draft-download-btn"
                (click)="downloadOriginalCover()"
                i18n-title="@@Download Original Cover PDF"
                title="Download Original Cover PDF">
          <mat-icon>download</mat-icon>
          <span i18n="@@Download Original Cover PDF">Download Original Cover PDF</span>
        </button>
        <button *ngIf="draft.draftBody?.resource_ID"
                mat-stroked-button
                color="primary"
                class="draft-download-btn"
                (click)="downloadOriginalBody()"
                i18n-title="@@Download Original Body PDF"
                title="Download Original Body PDF">
          <mat-icon>download</mat-icon>
          <span i18n="@@Download Original Body PDF">Download Original Body PDF</span>
        </button>
      </div>

      <!--
        ── File Status ──────────────────────────────────────
        Preparation state of the Draft's PDFs.

        This reports `Resource.prepared` ONLY. There is deliberately
        no "Standardize" control here, unlike the Book view: a Draft's
        draftBody / draftCover live in `mutablebody` / `mutablecover`,
        which have no `standardized` column, and every server-side
        standardization query joins `body` / `cover` to `book`. A
        button here would set a property nothing persists and nothing
        reads. Standardization is not skipped — `makeDraftIntoBook`
        carries this same resource onto the new Body / Cover and
        queues it — which is what the note at the bottom explains.

        Each row is `*ngIf`-guarded on `resource_ID` so a Draft that
        has only a cover or only a body during setup shows only the
        half that exists.
      -->
      <div class="draft-file-status" *ngIf="hasAnyResource">
        <div class="draft-file-status-header">
          <mat-icon>fact_check</mat-icon>
          <span i18n="@@File Status">File Status</span>
        </div>

        <div class="draft-file-status-row" *ngIf="draft.draftCover?.resource_ID">
          <span class="draft-file-status-label" i18n="@@Cover">Cover</span>
          <span class="resource-id-badge"
                [style.background-color]="getResourceStatusColor(draft.draftCover)"
                [class.res-badge-actionable]="isResourceActionable(draft.draftCover)"
                [attr.role]="isResourceActionable(draft.draftCover) ? 'button' : null"
                [attr.tabindex]="isResourceActionable(draft.draftCover) ? 0 : null"
                (click)="onResourceBadgeClick(draft.draftCover, $event)"
                (keydown.enter)="onResourceBadgeClick(draft.draftCover, $event)"
                (keydown.space)="onResourceBadgeClick(draft.draftCover, $event)"
                [title]="getResourceHoverText(draft.draftCover, 'Cover Resource ID')">{{getResourceDisplayString(draft.draftCover)}}</span>
          <span class="status-badge-inline"
                [ngClass]="getStatusClass(preparedStatus(draft.draftCover))"
                i18n-title="@@Cover Prepared"
                title="Cover Prepared">{{preparedStatusLabel(draft.draftCover)}}</span>
        </div>

        <div class="draft-file-status-row" *ngIf="draft.draftBody?.resource_ID">
          <span class="draft-file-status-label" i18n="@@Body">Body</span>
          <span class="resource-id-badge"
                [style.background-color]="getResourceStatusColor(draft.draftBody)"
                [class.res-badge-actionable]="isResourceActionable(draft.draftBody)"
                [attr.role]="isResourceActionable(draft.draftBody) ? 'button' : null"
                [attr.tabindex]="isResourceActionable(draft.draftBody) ? 0 : null"
                (click)="onResourceBadgeClick(draft.draftBody, $event)"
                (keydown.enter)="onResourceBadgeClick(draft.draftBody, $event)"
                (keydown.space)="onResourceBadgeClick(draft.draftBody, $event)"
                [title]="getResourceHoverText(draft.draftBody, 'Body Resource ID')">{{getResourceDisplayString(draft.draftBody)}}</span>
          <span class="status-badge-inline"
                [ngClass]="getStatusClass(preparedStatus(draft.draftBody))"
                i18n-title="@@Body Prepared"
                title="Body Prepared">{{preparedStatusLabel(draft.draftBody)}}</span>
        </div>

        <!-- Start preparation / in-flight indicator -->
        <div class="draft-file-status-row" *ngIf="canProcessFiles || filesPreparing">
          <button *ngIf="canProcessFiles"
                  mat-raised-button
                  color="primary"
                  (click)="processPdfFiles()"
                  i18n-title="@@Process PDF Files"
                  title="Process PDF Files">
            <mat-icon>play_circle_outline</mat-icon>
            <span i18n="@@Process PDF Files">Process PDF Files</span>
          </button>
          <mat-spinner *ngIf="filesPreparing" diameter="18"></mat-spinner>
          <span *ngIf="filesPreparing" class="draft-file-status-note" i18n="@@Files are being processed">Files are being processed</span>
        </div>

        <!--
          Shown once every PDF the Draft has is prepared — the point
          at which nothing more can happen at the Draft stage. Says
          where standardization actually occurs instead of leaving
          the operator waiting for a step that never runs here.
        -->
        <div class="draft-file-status-note draft-standardize-note" *ngIf="filesPrepared">
          <mat-icon>info</mat-icon>
          <span i18n="@@draftStandardizeNote">Preparation is the only file processing that applies to a Draft. Standardization runs automatically on this same PDF when the Draft is made into a Book.</span>
        </div>
      </div>

      <!--
        Resource preparation problems.

        Mirrors the panel in ProjectBookComponent, and is driven by
        `Resource.prepared === 'ERROR'` rather than by `problems` being
        non-empty — a failed resource can come back with an empty `problems`
        string, and reporting nothing in that case leaves the user with no
        explanation for why the Draft will not proceed. `problemNote` is
        informational and shows independently.

        Unlike the Book side, DraftCover / DraftBody carry only `resource_ID`,
        so `coverResource` / `bodyResource` resolve through ResourceService and
        trigger an on-demand fetch the first time they are asked for.
      -->
      <div *ngIf="hasResourceProblems" class="draft-problems-area">
        <div *ngIf="coverHasError" class="draft-problem-item draft-error-text">
          <mat-icon>warning</mat-icon>
          <span><strong i18n="@@Cover">Cover</strong>:
            <ng-container *ngIf="coverResource?.problems">{{coverResource.problems}}</ng-container>
            <ng-container *ngIf="!coverResource?.problems" i18n="@@No details were provided">No details were provided</ng-container>
          </span>
        </div>
        <div *ngIf="coverResource?.problemNote" class="draft-problem-item">
          <mat-icon>info</mat-icon>
          <span><strong i18n="@@Cover">Cover</strong>: {{coverResource.problemNote}}</span>
        </div>
        <div *ngIf="bodyHasError" class="draft-problem-item draft-error-text">
          <mat-icon>warning</mat-icon>
          <span><strong i18n="@@Body">Body</strong>:
            <ng-container *ngIf="bodyResource?.problems">{{bodyResource.problems}}</ng-container>
            <ng-container *ngIf="!bodyResource?.problems" i18n="@@No details were provided">No details were provided</ng-container>
          </span>
        </div>
        <div *ngIf="bodyResource?.problemNote" class="draft-problem-item">
          <mat-icon>info</mat-icon>
          <span><strong i18n="@@Body">Body</strong>: {{bodyResource.problemNote}}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Scrollable Cover/Body area -->
  <div class="draft-content-area" *ngIf="draft.draftBody">
    <ng-container *ngIf="displayCover">
      <app-resource #resourceComponent [draft]="draft"
        [resourceId]="draft.draftCover.resource_ID"
        [verticalShift]="draft.draftCover.verticalShift"
        [horizontalShift]="draft.draftCover.horizontalShift"
        [scale]="draft.draftCover.documentScale"
        [dpi]="288"
        [pageNumber]="1"
        [orgId]="draft.org_ID"
        [bodyOrCover]="'COVER'"
        [bindingType]="draft.bindingType"
        (shiftChanged)="onCoverShiftChanged($event)"
        (scaleChanged)="onCoverScaleChanged($event)"
        (trimChanged)="onCoverTrimChanged($event)"
        style="flex: 1 1 0; min-height: 0; overflow: auto;">
      </app-resource>
    </ng-container>
    <ng-container *ngIf="!displayCover">
      <app-project-draft-body #draftBodyComponent [draft]="draft" [draftBody]="draft.draftBody" style="display: flex; flex-direction: column; flex: 1 1 0; min-height: 0; overflow: hidden;"></app-project-draft-body>
    </ng-container>
  </div>
</div>
<!--
  No Draft at all (the pane is open with nothing selected). The
  "Draft exists but its sides have not loaded" case is handled INSIDE
  the root element above, so it can still show the upload status bar
  and a spinner; this branch used to swallow it and render an empty
  div, which is what a blank editor pane looked like.
-->
<div class="draft-none" *ngIf="!draft" i18n="@@No Draft"></div>
